import { Dispatch, SetStateAction, useState } from 'react';

import { OnboardingAccountView } from '@ndvr/customer-api';

import {
    accountTitle,
    beneficiaryRelationship,
    BeneficiaryRow,
    BeneficiaryValues,
    toBeneficiaryValues,
    validateBeneficiaries,
} from './account-model';
import { CheckBox, Field, GroupTitle, linkButtonClass, NativeSelect, RemoveButton } from './fields';
import { PersonKind } from './onboarding-model';
import { OnboardingHousehold } from './useOnboardingHousehold';

const LISTS = [
    { key: 'primary', title: 'Primary', add: 'Add primary' },
    { key: 'contingent', title: 'Contingent', add: 'Add contingent' },
] as const;

/**
 * An account's primary and contingent beneficiaries: person, derived relationship, share and per stirpes, with each
 * list's total; "Copy beneficiaries from…" takes another account's lists.
 */
export const BeneficiariesSection = ({
    household,
    account,
    owners,
    title,
    beneficiaries,
    onChange: setBeneficiaries,
    addPerson,
}: {
    household: OnboardingHousehold;
    account: OnboardingAccountView;
    owners: string[];
    title: string;
    beneficiaries: BeneficiaryValues;
    onChange: Dispatch<SetStateAction<BeneficiaryValues>>;
    addPerson: (kind: PersonKind, onCreated: (personOrgGUID: string) => void) => void;
}) => {
    const [copyOpen, setCopyOpen] = useState(false);
    // People of the household only; a trust or business does not inherit in onboarding
    const beneficiaryOptions = household
        .peopleOf(PersonKind.CLIENT, PersonKind.FAMILY)
        .filter((option) => !owners.includes(option.value));
    const copySources = household.accounts.filter(
        (other) =>
            other.account.accountGUID !== account.accountGUID &&
            [
                ...(other.account.beneficiaries?.primaryBeneficiaries ?? []),
                ...(other.account.beneficiaries?.contingentBeneficiaries ?? []),
            ].length > 0,
    );
    const setList = (key: keyof BeneficiaryValues, rows: BeneficiaryRow[]) =>
        setBeneficiaries((current) => ({ ...current, [key]: rows }));

    return (
        <div className="border-cocoa-100 flex flex-col gap-4 border-b py-[18px]">
            <div className="relative flex items-center justify-between gap-3">
                <GroupTitle>{title}</GroupTitle>
                {copySources.length > 0 && (
                    <button type="button" className={linkButtonClass} onClick={() => setCopyOpen(!copyOpen)}>
                        Copy beneficiaries from…
                    </button>
                )}
                {copyOpen && (
                    <div className="border-border absolute top-6 right-0 z-10 flex min-w-60 flex-col rounded-lg border bg-white p-1.5 shadow-lg">
                        {copySources.map((source) => (
                            <button
                                key={source.account.accountGUID}
                                type="button"
                                className="hover:bg-sand-100 cursor-pointer rounded px-2.5 py-2 text-left text-sm"
                                onClick={() => {
                                    setBeneficiaries(toBeneficiaryValues(source.account));
                                    setCopyOpen(false);
                                }}
                            >
                                {accountTitle(source.account)}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {LISTS.map((list) => {
                const rows = beneficiaries[list.key];
                const validation = validateBeneficiaries(rows);
                const warning =
                    validation.rows.flatMap(({ person, share }) => [person, share]).find(Boolean) ??
                    validation.totalError;
                return (
                    <div key={list.key} className="flex flex-col gap-2">
                        <span className="text-[13px] font-medium">{list.title}</span>
                        {rows.length === 0 ? (
                            <span className="text-cocoa-400 text-[13px]">None</span>
                        ) : (
                            <div className="text-cocoa-600 grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_96px_24px] gap-2.5 text-xs">
                                <span>Person</span>
                                <span>Relationship</span>
                                <span>Share %</span>
                                <span>Per stirpes</span>
                                <span />
                            </div>
                        )}
                        {rows.map((beneficiary, index) => {
                            const set = (patch: Partial<BeneficiaryRow>) =>
                                setList(
                                    list.key,
                                    rows.map((other, j) => (j === index ? { ...other, ...patch } : other)),
                                );
                            return (
                                <div
                                    key={index}
                                    className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_96px_24px] items-start gap-2.5"
                                >
                                    <Field missing={!beneficiary.personOrgGUID}>
                                        {(id) => (
                                            <NativeSelect
                                                id={id}
                                                aria-label="Beneficiary"
                                                value={beneficiary.personOrgGUID}
                                                invalid={!!validation.rows[index].person}
                                                placeholder="Select person…"
                                                options={[
                                                    ...beneficiaryOptions,
                                                    { value: 'new', label: 'Add new person…' },
                                                ]}
                                                onChange={(value) =>
                                                    value === 'new'
                                                        ? addPerson(PersonKind.FAMILY, (guid) =>
                                                              setBeneficiaries((current) => ({
                                                                  ...current,
                                                                  [list.key]: current[list.key].map((other, j) =>
                                                                      j === index
                                                                          ? { ...other, personOrgGUID: guid }
                                                                          : other,
                                                                  ),
                                                              })),
                                                          )
                                                        : set({ personOrgGUID: value })
                                                }
                                            />
                                        )}
                                    </Field>
                                    {/* Derived from the people: no relationship is stored on the beneficiary */}
                                    <span className="text-cocoa-600 text-sm">
                                        {beneficiaryRelationship(
                                            household.byGUID.get(beneficiary.personOrgGUID)?.personOrg,
                                            owners,
                                            household.relations,
                                        ) || '—'}
                                    </span>
                                    <Field missing={!beneficiary.share.trim()}>
                                        {(id) => (
                                            <input
                                                id={id}
                                                aria-label="Share %"
                                                inputMode="decimal"
                                                className={`h-9 rounded border px-2.5 text-sm ${validation.rows[index].share || validation.totalError ? 'border-gold-500' : 'border-border'}`}
                                                aria-invalid={!!(validation.rows[index].share || validation.totalError)}
                                                value={beneficiary.share}
                                                onChange={(event) =>
                                                    set({ share: event.target.value.replace(/[^\d.]/g, '') })
                                                }
                                            />
                                        )}
                                    </Field>
                                    <CheckBox
                                        checked={beneficiary.perStirpes}
                                        onChange={(perStirpes) => set({ perStirpes })}
                                    />
                                    <RemoveButton
                                        label="Remove beneficiary"
                                        onClick={() =>
                                            setList(
                                                list.key,
                                                rows.filter((_, j) => j !== index),
                                            )
                                        }
                                    />
                                </div>
                            );
                        })}
                        <div className="flex items-center justify-between gap-3">
                            <button
                                type="button"
                                className={linkButtonClass}
                                onClick={() =>
                                    setList(list.key, [
                                        ...rows,
                                        {
                                            personOrgGUID: '',
                                            share: rows.length ? '' : '100',
                                            perStirpes: false,
                                        },
                                    ])
                                }
                            >
                                {list.add}
                            </button>
                            {rows.length > 0 && (
                                <span
                                    className={
                                        !warning
                                            ? 'text-cocoa-600 text-[13px]'
                                            : 'text-gold-600 text-[13px] font-medium'
                                    }
                                >
                                    {warning ?? 'Total 100%'}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
